/* eslint-disable no-console */

/**
 * createToc.js - Refactored and Simplified
 *
 * Enhanced createToc function that supports both paginated and non-paginated contexts
 * Now focused solely on TOC generation, with hamburger menu functionality moved to separate modules
 */

import { debugLog } from "./index";
import createHamburgerMenu, {
  createPaginatedHamburgerMenu,
  createPreviewHamburgerMenu,
} from "./hamburgerMenu";
import injectHamburgerMenuStyles from "./hamburgerMenuStyles";

/**
 * Main TOC creation function
 *
 * @param {Object} config - Configuration object
 * @param {Element} config.content - The content element to search for headings
 * @param {string} config.tocElement - CSS selector for the TOC container
 * @param {Array} config.titleElements - Array of CSS selectors for headings to include
 * @param {boolean} config.removeFallbackOnlyIfHeadingsFound - Whether to remove fallback TOC only if headings are found
 * @param {boolean} config.singleLineEntries - Whether TOC entries should be single line
 * @param {boolean} config.showPageNumbers - Whether to show page number elements (for paginated contexts)
 * @param {boolean} config.showDots - Whether to show dots between title and page number
 * @param {string} config.context - Context identifier ('paginated', 'menu', 'preview', etc.)
 * @returns {Object|null} TOC creation result or null if failed
 */
function createToc(config) {
  const {
    content,
    tocElement,
    titleElements,
    removeFallbackOnlyIfHeadingsFound,
    singleLineEntries = true,
    showPageNumbers = true,
    showDots = true,
    context = "default",
  } = config;

  debugLog(`createToc called in ${context} context. config=`, config);

  // Validate required parameters
  if (!content || !tocElement || !titleElements) {
    console.error("createToc: Missing required parameters", {
      content: !!content,
      tocElement,
      titleElements,
    });
    return null;
  }

  // Find all matching headings first
  const allTocHeadings = findAllHeadings(content, titleElements);

  // Handle fallback TOC replacement/update
  handleFallbackToc(content, allTocHeadings, removeFallbackOnlyIfHeadingsFound);

  // Find your TOC container in the pre-pagination DOM
  const tocElementDiv = content.querySelector(tocElement);
  if (!tocElementDiv) {
    console.warn("No TOC container found for selector", tocElement);
    return null;
  }

  // If no headings found, stop here but keep fallback
  if (allTocHeadings.length === 0) {
    debugLog("No headings found, not creating a dynamic TOC");
    return null;
  }

  // Create and populate the TOC
  const tocResult = createTocStructure({
    tocElementDiv,
    headings: allTocHeadings,
    singleLineEntries,
    showPageNumbers,
    showDots,
    context,
  });

  debugLog(
    `TOC creation complete in ${context} context. ${allTocHeadings.length} headings processed.`
  );

  return tocResult;
}

/**
 * Finds all headings that match the specified selectors
 * IMPORTANT: Maintains document order by collecting all selectors at once
 *
 * @param {Element} content - Content element to search
 * @param {Array} titleElements - Array of CSS selectors
 * @returns {Array} Array of heading objects in document order
 */
function findAllHeadings(content, titleElements) {
  // Create a combined selector to maintain document order
  const combinedSelector = titleElements.join(", ");
  debugLog(`Looking for headings with combined selector "${combinedSelector}"`);

  const allNodes = content.querySelectorAll(combinedSelector);
  debugLog(`Found ${allNodes.length} total heading(s) in document order`);

  // Map each node to its level based on which selector it matches
  const allTocHeadings = Array.from(allNodes).map(node => {
    // Find which selector this node matches to determine its level
    let level = 1; // default

    for (let i = 0; i < titleElements.length; i++) {
      if (node.matches(titleElements[i])) {
        level = i + 1;
        break;
      }
    }

    return {
      element: node,
      level: level,
    };
  });

  debugLog(
    `Processed ${allTocHeadings.length} headings with levels:`,
    allTocHeadings.map(
      h => `${h.element.textContent?.substring(0, 30)}... (level ${h.level})`
    )
  );

  return allTocHeadings;
}

/**
 * Handles the fallback TOC replacement or update
 *
 * @param {Element} content - Content element
 * @param {Array} allTocHeadings - Found headings
 * @param {boolean} removeFallbackOnlyIfHeadingsFound - Whether to conditionally remove fallback
 */
function handleFallbackToc(
  content,
  allTocHeadings,
  removeFallbackOnlyIfHeadingsFound
) {
  const fallbackToc = content.querySelector("#my-toc-fallback");

  const shouldRemoveFallback =
    !removeFallbackOnlyIfHeadingsFound ||
    (removeFallbackOnlyIfHeadingsFound && allTocHeadings.length > 0);

  if (shouldRemoveFallback && fallbackToc) {
    debugLog(
      `Replacing fallback TOC with simplified level 1 TOC (found ${allTocHeadings.length} headings)`
    );

    // Clear the fallback TOC content
    fallbackToc.innerHTML = "";

    // Create a simplified TOC for level 1 headings only
    const level1Headings = allTocHeadings.filter(item => item.level === 1);

    if (level1Headings.length > 0) {
      const simpleTocUl = createSimpleFallbackToc(level1Headings);
      fallbackToc.appendChild(simpleTocUl);
      debugLog("Created simplified level 1 TOC in the fallback container");
    } else {
      debugLog("No level 1 headings found, leaving fallback TOC empty");
    }
  } else if (fallbackToc) {
    debugLog(`Keeping fallback TOC (found ${allTocHeadings.length} headings)`);
  }
}

/**
 * Creates a simple fallback TOC for cover pages
 *
 * @param {Array} level1Headings - Array of level 1 headings
 * @returns {Element} Simple TOC list element
 */
function createSimpleFallbackToc(level1Headings) {
  const simpleTocUl = document.createElement("ul");
  simpleTocUl.classList.add("list-cover-toc");
  simpleTocUl.style.listStyle = "none";
  simpleTocUl.style.paddingLeft = "0";

  level1Headings.forEach((item, idx) => {
    const element = item.element;

    // Generate a valid CSS selector ID
    const validId = generateValidId(element.innerHTML, idx + 1);

    // Set the heading ID
    if (!element.id) {
      element.id = validId;
    }

    // Create a list item for this heading
    const tocLi = document.createElement("li");
    tocLi.style.margin = "10px 0";

    // Create a simple anchor with no page numbers or dots
    const anchor = document.createElement("a");
    anchor.href = `#${element.id}`;
    anchor.innerHTML = element.innerHTML;
    anchor.style.textDecoration = "none";
    anchor.style.color = "inherit";

    tocLi.appendChild(anchor);
    simpleTocUl.appendChild(tocLi);
  });

  return simpleTocUl;
}

/**
 * Creates the main TOC structure
 *
 * @param {Object} config - Configuration object
 * @returns {Object} TOC creation result
 */
function createTocStructure(config) {
  const {
    tocElementDiv,
    headings,
    singleLineEntries,
    showPageNumbers,
    showDots,
    context,
  } = config;

  // Create a UL for the dynamic TOC and append it
  const tocUl = document.createElement("ul");
  tocUl.classList.add("list-toc-generated");

  // Add context-specific classes
  tocUl.classList.add(`toc-context-${context}`);
  if (!showPageNumbers) {
    tocUl.classList.add("toc-no-page-numbers");
  }
  if (!showDots) {
    tocUl.classList.add("toc-no-dots");
  }

  tocElementDiv.appendChild(tocUl);
  debugLog("Created <ul> in the TOC container", tocUl);

  // Process headings and add IDs
  const processedHeadings = processHeadings(headings);

  // Build TOC entries
  buildTocEntries(
    tocUl,
    processedHeadings,
    context,
    singleLineEntries,
    showPageNumbers,
    showDots
  );

  debugLog(
    "Done building TOC. <ul> should have child <li> entries now.",
    tocUl
  );

  // Return the created TOC element for further manipulation if needed
  return {
    tocElement: tocUl,
    headingCount: headings.length,
    context: context,
    headings: processedHeadings.map(item => ({
      element: item.element,
      level: item.level,
      id: item.element.id,
      text: item.element.textContent || item.element.innerHTML,
    })),
  };
}

/**
 * Processes headings by adding classes and IDs
 *
 * @param {Array} headings - Array of heading objects
 * @returns {Array} Processed headings with IDs
 */
function processHeadings(headings) {
  const usedIds = new Set();
  let tocElementNbr = 0;

  return headings.map(item => {
    const { element } = item;
    const { level } = item;

    element.classList.add("title-element");
    element.setAttribute("data-title-level", level);

    tocElementNbr += 1;

    // Generate a valid CSS selector ID
    const validId = generateValidId(element.innerHTML, tocElementNbr);

    // Ensure uniqueness
    if (usedIds.has(validId)) {
      console.warn(`Duplicate ID detected: ${validId}`);
    } else {
      usedIds.add(validId);
    }

    // Set the heading ID
    if (!element.id) {
      element.id = validId;
    }

    debugLog(
      "Marked heading:",
      element.tagName,
      "ID:",
      element.id,
      "data-title-level:",
      level
    );

    return { element, level };
  });
}

/**
 * Builds TOC entries in the provided UL element
 *
 * @param {Element} tocUl - TOC list element
 * @param {Array} processedHeadings - Processed heading objects
 * @param {string} context - TOC context
 * @param {boolean} singleLineEntries - Whether entries should be single line
 * @param {boolean} showPageNumbers - Whether to show page numbers
 * @param {boolean} showDots - Whether to show dots
 */
function buildTocEntries(
  tocUl,
  processedHeadings,
  context,
  singleLineEntries,
  showPageNumbers,
  showDots
) {
  processedHeadings.forEach(({ element: heading }) => {
    const tocNewLi = createTocListItem(heading, context);
    const mainAnchor = createTocAnchor(
      heading,
      context,
      singleLineEntries,
      showPageNumbers,
      showDots
    );

    tocNewLi.appendChild(mainAnchor);
    tocUl.appendChild(tocNewLi);

    debugLog(
      "Appended new TOC list item for heading:",
      heading.id,
      "with text:",
      heading.innerText,
      "context:",
      context
    );
  });
}

/**
 * Creates a TOC list item element
 *
 * @param {Element} heading - Heading element
 * @param {string} context - TOC context
 * @returns {Element} List item element
 */
function createTocListItem(heading, context) {
  const tocNewLi = document.createElement("li");
  tocNewLi.classList.add("toc-element");
  tocNewLi.classList.add(`toc-element-level-${heading.dataset.titleLevel}`);

  // Add break-after-avoid class to level 1 headings in the TOC (only for paginated contexts)
  if (heading.dataset.titleLevel === "1" && context === "paginated") {
    tocNewLi.classList.add("break-after-avoid");
  }

  // Copy classes from the heading to the TOC item, filtering out break-related classes
  heading.classList.forEach(cls => {
    if (
      cls !== "title-element" &&
      !cls.includes("break-after") &&
      !cls.includes("break-before") &&
      !cls.includes("break-inside")
    ) {
      tocNewLi.classList.add(cls);
    }
  });

  // Store the heading's ID in data-target
  tocNewLi.setAttribute("data-target", `#${heading.id}`);

  return tocNewLi;
}

/** Strip the inner tooltip bubble from a heading’s HTML */
function stripTooltipText(html) {
  return html.replace(
    /<span[^>]*class="[^"]*\btooltiptext\b[^"]*"[^>]*>[\s\S]*?<\/span>/gi,
    ""
  );
}

/**
 * Creates a TOC anchor element
 *
 * @param {Element} heading - Heading element
 * @param {string} context - TOC context
 * @param {boolean} singleLineEntries - Whether entries should be single line
 * @param {boolean} showPageNumbers - Whether to show page numbers
 * @param {boolean} showDots - Whether to show dots
 * @returns {Element} Anchor element
 */
function createTocAnchor(
  heading,
  context,
  singleLineEntries,
  showPageNumbers,
  showDots
) {
  const mainAnchor = document.createElement("a");
  mainAnchor.href = `#${heading.id}`;

  // Configure layout based on context
  if (context === "paginated" || showPageNumbers || showDots) {
    // Full TOC with consistent flex layout for all levels (paginated context)
    mainAnchor.style.display = "flex";
    mainAnchor.style.width = "100%";
    mainAnchor.style.alignItems = "baseline";
  } else {
    // Simple TOC for menu/preview contexts
    mainAnchor.style.display = "block";
    mainAnchor.style.textDecoration = "none";
  }

  // Create the title span
  const titleSpan = document.createElement("span");
  titleSpan.classList.add("toc-title");
  if (!singleLineEntries) {
    titleSpan.classList.add("multi-line");
  }
  titleSpan.innerHTML = stripTooltipText(heading.innerHTML);
  mainAnchor.appendChild(titleSpan);

  // Add dots and page numbers based on context and configuration
  if (context === "paginated") {
    // Always add dots and page spans for consistent layout in paginated context
    addDotsSpan(mainAnchor);
    addPageSpan(mainAnchor, heading.id);
  } else {
    // For non-paginated contexts, only add if specifically requested
    if (showDots) {
      addDotsSpan(mainAnchor);
    }
    if (showPageNumbers) {
      addPageSpan(mainAnchor, heading.id);
    }
  }

  return mainAnchor;
}

/**
 * Adds a dots span to the anchor
 *
 * @param {Element} anchor - Anchor element
 */
function addDotsSpan(anchor) {
  const dotsSpan = document.createElement("span");
  dotsSpan.classList.add("toc-dots");
  anchor.appendChild(dotsSpan);
}

/**
 * Adds a page span to the anchor
 *
 * @param {Element} anchor - Anchor element
 * @param {string} headingId - Heading ID for the data-href attribute
 */
function addPageSpan(anchor, headingId) {
  const pageSpan = document.createElement("span");
  pageSpan.classList.add("toc-page");
  pageSpan.setAttribute("data-href", `#${headingId}`);
  anchor.appendChild(pageSpan);
}

/**
 * Generate a valid CSS selector ID from the heading text
 *
 * @param {string} text - The heading text
 * @param {number} index - A unique index to ensure uniqueness
 * @returns {string} Valid CSS selector ID
 */
function generateValidId(text, index) {
  // First extract just the text content without HTML tags
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = text;
  const plainText = tempDiv.textContent || tempDiv.innerText || "";

  // Create a CSS-valid slug
  // CSS identifiers must begin with a letter, underscore, or hyphen
  // And can only contain letters, numbers, underscores, or hyphens
  let slug = plainText
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s\-_]/g, "") // Only keep valid CSS ID characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Remove consecutive hyphens

  // Ensure it starts with a letter (prepend 'toc-' if needed)
  if (!slug.match(/^[a-z]/)) {
    slug = "toc-" + slug;
  }

  // Add the index to ensure uniqueness
  slug = `${slug}-${index}`;

  return slug;
}

/**
 * Convenience function to create a TOC for paginated contexts (with page numbers and dots)
 *
 * @param {Object} config - Configuration object
 * @returns {Object|null} TOC creation result
 */
function createPaginatedToc(config) {
  return createToc({
    ...config,
    showPageNumbers: true,
    showDots: true,
    context: "paginated",
  });
}

/**
 * Convenience function to create a TOC for menu contexts (simple links only)
 *
 * @param {Object} config - Configuration object
 * @returns {Object|null} TOC creation result
 */
function createMenuToc(config) {
  return createToc({
    ...config,
    showPageNumbers: false,
    showDots: false,
    context: "menu",
  });
}

/**
 * Convenience function to create a TOC for preview contexts (simple links only)
 *
 * @param {Object} config - Configuration object
 * @returns {Object|null} TOC creation result
 */
function createPreviewToc(config) {
  return createToc({
    ...config,
    showPageNumbers: false,
    showDots: false,
    context: "preview",
  });
}

/**
 * Convenience function to create a TOC with an integrated hamburger menu
 * This combines TOC creation with hamburger menu creation for non-paginated contexts
 *
 * @param {Object} config - Configuration object
 * @returns {Object|null} Combined TOC and hamburger menu result
 */
function createTocWithHamburgerMenu(config) {
  const { container, ...tocConfig } = config;

  // First create the TOC to get the headings
  const tocResult = createToc({
    ...tocConfig,
    showPageNumbers: false,
    showDots: false,
    context: "menu",
  });

  if (!tocResult || !tocResult.headings) {
    console.warn("No TOC created or no headings found for hamburger menu");
    return tocResult;
  }

  // Inject hamburger menu styles
  injectHamburgerMenuStyles();

  // Create hamburger menu with the headings
  const hamburgerMenu = createPreviewHamburgerMenu({
    headings: tocResult.headings,
    container: container || document.body,
  });

  // Return combined result
  return {
    ...tocResult,
    hamburgerMenu,
  };
}

/**
 * Creates a TOC with hamburger menu for paginated contexts
 * This creates both a traditional TOC and a hamburger menu for screen viewing
 *
 * @param {Object} config - Configuration object
 * @returns {Object|null} Combined result
 */
function createPaginatedTocWithHamburger(config) {
  const { container, ...tocConfig } = config;

  // Create paginated TOC first
  const tocResult = createPaginatedToc(tocConfig);

  if (!tocResult || !tocResult.headings) {
    console.warn("No paginated TOC created for hamburger menu");
    return tocResult;
  }

  // Inject hamburger menu styles
  injectHamburgerMenuStyles();

  // Create hamburger menu for screen viewing (hidden on print)
  const hamburgerMenu = createPaginatedHamburgerMenu({
    headings: tocResult.headings,
    container: container || document.body,
  });

  return {
    ...tocResult,
    hamburgerMenu,
  };
}

/**
 * Helper function to create heading navigation props
 * This ensures consistent ID generation between TOC and HeadingWrapper
 *
 * @param {string} text - The heading text
 * @param {string} type - The heading type ('chapter' or 'topic')
 * @param {string|number} identifier - Unique identifier
 * @returns {Object} Navigation props object
 */
function createHeadingNavProps(text, type, identifier) {
  if (!text || !type || !identifier) {
    return { hasValidLink: false };
  }

  // Create a consistent ID that matches the TOC generation logic
  /* -----------------------------------------------------------
   * 1.  Normalise `text` → plain string
   * --------------------------------------------------------- */
  let plain = "";
  if (typeof text === "string") {
    plain = text;
  } else if (text != null) {
    /*  Either a React node, an HTML fragment, or something else
        we can stringify.  Fall back to DOM text extraction so
        we keep “<b>Bold</b> Title” → "Bold Title".              */
    try {
      const div = document.createElement("div");
      div.innerHTML = String(text);
      plain = div.textContent || div.innerText || "";
    } catch (_) {
      /* SSR or unexpected object - just coerce */
      plain = String(text);
    }
  }

  /* -----------------------------------------------------------
   * 2.  Build the slug exactly as before, but on `plain`
   * --------------------------------------------------------- */
  const baseSlug = plain
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s\-_]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

  const finalId = baseSlug.match(/^[a-z]/) ? baseSlug : `toc-${baseSlug}`;
  const id = `${finalId}-${identifier}`;

  return {
    hasValidLink: true,
    id,
    href: `#${id}`,
  };
}

/**
 * Ensures all headings in the content have proper IDs for TOC linking
 * This can be called independently if you just need to add IDs without creating a TOC
 *
 * @param {Element} content - Content element to process
 * @param {Array} titleElements - Array of CSS selectors for headings
 * @returns {Array} Array of processed headings with IDs
 */
function ensureHeadingIds(content, titleElements) {
  const headings = findAllHeadings(content, titleElements);
  return processHeadings(headings);
}

// Export the main function and convenience functions
export default createToc;
export {
  createPaginatedToc,
  createMenuToc,
  createPreviewToc,
  createTocWithHamburgerMenu,
  createPaginatedTocWithHamburger,
  createHeadingNavProps,
  ensureHeadingIds,
  generateValidId,
};
